/**
 * Shared Subscription Services
 *
 * This file contains API hooks that are shared across multiple components.
 * Component-specific hooks have been moved to their respective services:
 * - List: components/list/services/listApi.ts
 * - Templates: components/templates/services/templateApi.ts
 * - Details: components/details/services/detailApi.ts
 * - Wizard: components/wizard/services/wizardApi.ts
 */

import { useQuery } from "@tanstack/react-query";
import { fetchSubscriptionMetrics as fetchSubscriptionMetricsApi } from "@/helpers/apis";
import { QUERY_OPTIONS } from "./queryConfig";
import { flattenMetricsCatalog } from "./normalizers";
import { useAppState } from "@/modules/app/useAppContext";
import type { CatalogPlatform } from "./types";

// Re-export types for convenience
export type {
  SubscriptionV2Template,
  SubscriptionV2Variable,
  SubscriptionHistoryItem,
  SubscriptionDetail,
  MetricDefinition,
  SubscriptionMetricsResponse,
} from "./types";

export const useSubscriptionMetrics = () => {
  const { isDatabricksTenant } = useAppState();
  const platform: CatalogPlatform = isDatabricksTenant ? "databricks" : "snowflake";

  return useQuery({
    queryKey: ["subscription-metrics", platform],
    queryFn: async () => {
      const catalog = await fetchSubscriptionMetricsApi({ platform });
      return catalog ? flattenMetricsCatalog(catalog, platform) : [];
    },
    ...QUERY_OPTIONS.reference,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
